import { ROLES } from "../../constants/base";
import { isOwnOperation, pickNextOperations } from "../Home/nextOperationsRows";

// Pure logic behind the lots list: phases, filters, the next operation of each lot, pages.

// Mirrors BATCH_SUBSTATES.WINE's aging entries in
// services/operations/helpers/vinificationConstants.js (server).
export const AGING_SUBSTATES = [
  "AGING_STEEL",
  "AGING_CONCRETE",
  "AGING_WOOD",
  "AGING_BARREL",
  "AGING_LARGE_BARREL",
  "AGING_AMPHORA",
];

// Phases of the "Fase" select, in life-cycle order. They do not overlap: a wine lot that is aging is
// AGING, not WINE, so the counts add up to the total.
export const PHASES = ["GRAPE", "MUST", "WINE", "AGING", "BOTTLED", "CLOSED"];

const isAging = (lot) =>
  AGING_SUBSTATES.includes(lot?.substate) ||
  (lot?.tank_substates || []).some(
    (ts) => AGING_SUBSTATES.includes(ts?.substate) && !ts?.substate_ended_at,
  );

export const getLotPhase = (lot) => {
  if (lot?.state === "WINE" && isAging(lot)) return "AGING";
  return lot?.state;
};

export const countByPhase = (lots) => {
  const counts = {
    ALL: 0,
    ...Object.fromEntries(PHASES.map((phase) => [phase, 0])),
  };
  (lots || []).forEach((lot) => {
    counts.ALL += 1;
    const phase = getLotPhase(lot);
    if (phase in counts) counts[phase] += 1;
  });
  return counts;
};

// Planned lots show the planned quantity; grapes are weighed in Kg, everything else is in litres.
export const getLotQuantity = (lot) => ({
  value:
    Number(
      lot?.status === "PLANNED" ? lot?.planned_quantity : lot?.actual_quantity,
    ) || 0,
  unit: lot?.state === "GRAPE" ? "Kg" : "L",
});

// Substate of the lot; when its tanks differ, the distinct ones of the tanks.
export const getLotSubstates = (lot) => {
  if (lot?.has_mixed_substates && lot?.tank_substates?.length > 0) {
    return [
      ...new Set(lot.tank_substates.map((ts) => ts?.substate).filter(Boolean)),
    ];
  }
  return lot?.substate ? [lot.substate] : [];
};

export const getLotTechniques = (lot) => {
  const fromTanks = (lot?.tank_substates || [])
    .map((ts) => ts?.vinification_technique)
    .filter(Boolean);
  return [
    ...new Set([lot?.vinification_technique, ...fromTanks].filter(Boolean)),
  ];
};

// One entry per variety/year/origin, percentages rounded to add up to 100:
// { variety: "Sangiovese (67%)", origin: "Toscana", year: 2025 }.
export const getLotCompositionParts = (lot, getLabel) => {
  const merged = new Map();
  (lot?.composition || []).forEach(({ variety, year, origin, percentage }) => {
    const key = `${variety}|${year}|${origin}`;
    merged.set(key, {
      variety,
      year,
      origin,
      percentage:
        (merged.get(key)?.percentage || 0) + (Number(percentage) || 0),
    });
  });

  const entries = [...merged.values()];
  let total = 0;
  return entries.map(({ variety, year, origin, percentage }, index) => {
    const rounded =
      index === entries.length - 1 ? 100 - total : Math.round(percentage);
    total += rounded;
    return {
      variety:
        `${variety ? getLabel(`lotVarieties_${variety}`) : ""} (${rounded}%)`.trim(),
      origin: origin || "",
      year: year || "",
    };
  });
};

// Same entries as one line each, for the search: "Sangiovese (67%), Toscana [2025]".
export const getLotComposition = (lot, getLabel) =>
  getLotCompositionParts(lot, getLabel).map(
    ({ variety, origin, year }) =>
      `${variety}${origin ? `, ${origin}` : ""}${year ? ` [${year}]` : ""}`,
  );

const normalize = (text) =>
  String(text ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const tankNames = (lot) =>
  (lot?.tanks || [])
    .map((tank) => tank?.tank_name || tank?.name)
    .filter(Boolean);

const searchText = (lot, getLabel) =>
  normalize(
    [lot?.name, ...tankNames(lot), ...getLotComposition(lot, getLabel)].join(
      " ",
    ),
  );

// filters: { search, phase, substate, color, label, technique, tank, quantityMin, quantityMax, status }; empty = no filter.
export const filterLots = (lots, filters = {}, getLabel = (key) => key) => {
  const {
    search,
    phase,
    substate,
    color,
    label,
    technique,
    tank,
    quantityMin,
    quantityMax,
    status,
  } = filters;
  const needle = normalize(search).trim();
  const tankNeedle = normalize(tank).trim();
  const hasMin =
    quantityMin !== undefined && quantityMin !== "" && quantityMin !== null;
  const hasMax =
    quantityMax !== undefined && quantityMax !== "" && quantityMax !== null;

  return (lots || []).filter((lot) => {
    if (phase && phase !== "ALL" && getLotPhase(lot) !== phase) return false;
    if (substate && !getLotSubstates(lot).includes(substate)) return false;
    if (color && lot?.color !== color) return false;
    if (label && lot?.label !== label) return false;
    if (technique && !getLotTechniques(lot).includes(technique)) return false;
    if (status && lot?.status !== status) return false;
    if (
      tankNeedle &&
      !tankNames(lot).some((name) => normalize(name).includes(tankNeedle))
    )
      return false;
    if (hasMin || hasMax) {
      const { value } = getLotQuantity(lot);
      if (hasMin && value < Number(quantityMin)) return false;
      if (hasMax && value > Number(quantityMax)) return false;
    }
    if (needle && !searchText(lot, getLabel).includes(needle)) return false;
    return true;
  });
};

// Map lotId (or, with idField "tank_id", tankId) -> most urgent open operation touching it (expired first, then by date, priority, id).
// A USER only sees the operations assigned to them, as on the Home.
export const nextOperationByLot = ({
  operations,
  expiredOperations,
  role,
  userId,
  now = new Date(),
  idField = "batch_id",
}) => {
  const sorted = pickNextOperations({
    operations,
    expiredOperations,
    role,
    userId,
    now,
    limit: Infinity,
  });
  const byLot = new Map();
  sorted.forEach((operation) => {
    [...(operation?.src_tanks || []), ...(operation?.dest_tanks || [])].forEach(
      (entry) => {
        const lotId = entry?.[idField];
        if (lotId != null && !byLot.has(lotId)) byLot.set(lotId, operation);
      },
    );
  });
  return byLot;
};

// Map lotId -> operations running on that lot: started (start_date set, e.g. filtering, stabilization, oxygenation)
// and not completed. A USER only sees the ones assigned to them, as for the next operation.
export const startedOperationsByLot = ({
  operations,
  expiredOperations,
  role,
  userId,
  idField = "batch_id",
}) => {
  const byLot = new Map();
  const seen = new Set();
  [...(operations || []), ...(expiredOperations || [])].forEach((operation) => {
    if (
      !operation?.start_date ||
      operation?.completion_date ||
      seen.has(operation.id)
    )
      return;
    if (Number(role) === ROLES.USER && !isOwnOperation(operation, userId))
      return;
    seen.add(operation.id);
    [...(operation.src_tanks || []), ...(operation.dest_tanks || [])].forEach(
      (entry) => {
        const lotId = entry?.[idField];
        if (lotId == null) return;
        const list = byLot.get(lotId) || [];
        if (!list.some((o) => o.id === operation.id)) list.push(operation);
        byLot.set(lotId, list);
      },
    );
  });
  return byLot;
};

export const paginate = (rows, page, size) => {
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / size));
  const current = Math.min(Math.max(0, page), pageCount - 1);
  const start = current * size;
  return {
    rows: rows.slice(start, start + size),
    page: current,
    pageCount,
    total,
    from: total === 0 ? 0 : start + 1,
    to: Math.min(start + size, total),
  };
};
